import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Download, Menu } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { useAuth } from "@/hooks/useAuth";
import { isNannyUser } from "@/types/user";
import { Payslip, PayslipsResponse } from "@/types/employees";
import MenuBar from "@/components/MenuBar";

interface Props {
  employeeId?: number; // Optional - if not provided, uses logged-in user's ID
  isDashboard?: boolean; // Optional - if true, shows a condensed view
}

const EmployeePayslips: React.FC<Props> = ({
  employeeId,
  isDashboard = false,
}) => {
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPayslips();
  }, [employeeId]);

  const fetchPayslips = async () => {
    try {
      // If no employeeId provided and user is a nanny, use their employer employee ID
      const targetId =
        employeeId ||
        (user && isNannyUser(user) ? user.employerEmployeeId : null);

      if (!targetId) {
        throw new Error("No employee ID available");
      }

      const response = await ApiService.request<PayslipsResponse>(
        EndpointKeys.GET_EMPLOYEE_PAYSLIPS,
        {
          urlParams: { employeeId: targetId },
        },
      );

      setPayslips(response.payslips);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch payslips",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (plivId: number) => {
    try {
      await ApiService.downloadBlob(EndpointKeys.DOWNLOAD_PAYSTUB, {
        urlParams: { id: plivId },
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download payslip",
        variant: "destructive",
      });
    }
  };

  const renderPayslipsTable = () => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Pay Period</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {payslips.map((payslip) => (
          <TableRow key={payslip.title}>
            <TableCell>{payslip.title}</TableCell>
            <TableCell className="text-right">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownload(payslip.plivId)}
              >
                <Download className="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isDashboard) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Payslips</CardTitle>
          <CardDescription>
            View and download your recent payslips
          </CardDescription>
        </CardHeader>
        <CardContent>
          {payslips.length > 0 ? (
            renderPayslipsTable()
          ) : (
            <p className="text-center text-gray-500 py-8">
              No payslips available
            </p>
          )}
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={() => navigate("/payslips")}>
              View All Payslips
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0 p-4 md:p-8">
        <div className="max-w-7xl">
          <Card>
            <CardHeader>
              <CardTitle>Payslips</CardTitle>
              <CardDescription>
                View and download all your payslips
              </CardDescription>
            </CardHeader>
            <CardContent>
              {payslips.length > 0 ? (
                renderPayslipsTable()
              ) : (
                <p className="text-center text-gray-500 py-8">
                  No payslips available
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EmployeePayslips;
